import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { FindingCard } from './FindingCard';
import { FindingGroup } from './FindingGroup';
import { DerivedCompliance, formatDateTime, pluralize } from './complianceDerived';
import { styleSheet } from './styles';

const runMark = (run: Api.CampaignComplianceRun) => {
	if (run.errorMessage) {
		return '⚠';
	}
	return run.passed ? '✓' : '✕';
};

const runOutcome = (run: Api.CampaignComplianceRun, isLatestRun: boolean, blockerCount: number) => {
	if (run.errorMessage) {
		return `Incomplete — ${run.errorMessage}`;
	}
	if (run.passed) {
		return 'Passed';
	}
	// Runs carry no per-run finding counts; the outstanding blockers describe only the latest completed run.
	return isLatestRun ? pluralize(blockerCount, 'blocker') : 'Failed';
};

export const HistorySection = ({ compliance }: { compliance: DerivedCompliance }) => {
	const { runs, overrides, latestRun, resolved, blockers } = compliance;
	if (!runs.length && !resolved.length) {
		return null;
	}
	const headerBits: string[] = [];
	if (latestRun) {
		headerBits.push(`Last reviewed ${formatDateTime(latestRun.ranAt)}`);
		headerBits.push(pluralize(runs.length, 'run'));
	} else {
		headerBits.push('Never reviewed');
	}
	if (overrides.length) {
		headerBits.push(pluralize(overrides.length, 'release'));
	}
	return (
		<section className={css(styleSheet.section)}>
			<div className={css(styleSheet.sectionHead)}>
				<h3 className={css(styleSheet.sectionTitle)}>History</h3>
				<span className={css(styleSheet.sectionCount)}>{headerBits.join(' · ')}</span>
			</div>
			{resolved.length ? (
				<FindingGroup label='Already fixed' count={`(${resolved.length})`}>
					{resolved.map(finding => (
						<FindingCard key={finding.code} finding={finding} />
					))}
				</FindingGroup>
			) : null}
			{runs.length ? (
				<FindingGroup label='Every review run' count={`(${runs.length})`}>
					<div className={css(styleSheet.historyScroller)}>
						<table className={css(styleSheet.historyTable)}>
							<thead>
								<tr>
									<th className={css(styleSheet.historyHeadCell)}>When</th>
									<th className={css(styleSheet.historyHeadCell)}>Trigger</th>
									<th className={css(styleSheet.historyHeadCell)}>Outcome</th>
								</tr>
							</thead>
							<tbody>
								{[...runs].reverse().map((run, index) => {
									const release = overrides.find(override => override.runId && override.runId === run.id);
									return (
										<React.Fragment key={run.id ?? `run-${index}`}>
											<tr>
												<td className={css(styleSheet.historyCell, styleSheet.historyWhen)}>
													{runMark(run)} {formatDateTime(run.ranAt)}
												</td>
												<td className={css(styleSheet.historyCell)}>{run.trigger}</td>
												<td className={css(styleSheet.historyCell)}>
													{runOutcome(run, run === latestRun, blockers.length)}
												</td>
											</tr>
											{release ? (
												<tr>
													<td
														className={css(
															styleSheet.historyCell,
															styleSheet.historyWhen,
															styleSheet.historyReleaseCell
														)}
													>
														⚑ {formatDateTime(release.at)}
													</td>
													<td className={css(styleSheet.historyCell, styleSheet.historyReleaseCell)}>Release</td>
													<td className={css(styleSheet.historyCell, styleSheet.historyReleaseCell)}>
														Released by {release.by ?? 'an admin'} against this run
													</td>
												</tr>
											) : null}
										</React.Fragment>
									);
								})}
							</tbody>
						</table>
					</div>
				</FindingGroup>
			) : null}
		</section>
	);
};
